import React, { useState, useMemo, useEffect } from 'react';
import propTypes from 'prop-types';
import { ZipCodeForm } from '../ZipCodeForm';
import { StoreList } from '../StoreList';
import { AnimatedTireLogo } from '../LoadingIndicators/AnimatedTireLogo';
import { toggleLoadingSkeleton } from '../../utilities/toggleLoadingSkeleton';

const RECORD_COUNT = 10;
const loadState = Object.freeze({
  EMPTY: 'empty',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
});

const getStoreList = async (zipCode, offset) => {
  const response = await fetch(`/api/store-information/v2/list-stores?user_location=${zipCode}&offset=${offset}&limit=${RECORD_COUNT}`);
  if (!response.ok) {
    throw new Error('Unable to search for stores.');
  }

  const stores = await response.json();

  return stores;
};

const getSessionToken = async () => {
  const response = await fetch('/session/token');
  const token = await response.text();

  return token;
};

const StoreSelector = ({ zipCode, setZipCode }) => {
  const [shouldPrefetchStores, setShouldPrefetchStores] = useState(true);
  const [searchedZipCode, setSearchedZipCode] = useState(zipCode);
  const [storeList, setStoreList] = useState(null);
  const [loadingState, setLoadingState] = useState(loadState.EMPTY);
  const [offset, setOffset] = useState(0);
  const sessionToken = useMemo(getSessionToken, []);

  const fetchStores = async (zip) => {
    setLoadingState(loadState.LOADING);
    try {
      const stores = await getStoreList(zip, offset);
      setStoreList(stores);
      setSearchedZipCode(zip);
      setLoadingState(loadState.LOADED);
    } catch {
      setLoadingState(loadState.ERROR);
    }
  };

  // fetch stores when the modal is opened if the banner's zip code is valid
  // subsequent searches should only happen on intentional form submissions
  useEffect(() => {
    if (zipCode?.length === 5 && shouldPrefetchStores) {
      fetchStores(zipCode);
    }

    setShouldPrefetchStores(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCode, shouldPrefetchStores]);

  const handleStoreSelection = async (store) => {
    toggleLoadingSkeleton(true);
    try {
      const response = await fetch('/api/store-information/v2/select-store', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': await sessionToken,
        },
        body: JSON.stringify(store),
      });

      if (response.ok) {
        window.location.reload(true);
      } else {
        setLoadingState(loadState.ERROR);
        throw new Error('Unable to select a store.');
      }
    } catch {
      toggleLoadingSkeleton(false);
    }
  };

  const handleLoadMore = async () => {
    try {
      const zip = searchedZipCode;
      const updatedOffset = offset + RECORD_COUNT;
      const stores = await getStoreList(zip, updatedOffset);
      setStoreList(storeList.concat(stores));
      setSearchedZipCode(zip);
      setOffset(updatedOffset);
    } catch {
      setLoadingState(loadState.ERROR);
    }
  };

  return (
    <>
      <div className="modal__heading-section">
        <p>
          {`In order to give you accurate pricing and the best possible
          experience, we'll need your location.`}
        </p>
        <ZipCodeForm zipCode={zipCode} setZipCode={setZipCode} onSubmit={fetchStores} />
      </div>
      {loadingState === loadState.ERROR && (
        <div className="modal__overflow-section">
          <p className="cmp-store-card__store-count cmp-store-card__store-count--error" aria-live="polite">
            Hmm, something went wrong. Please try your search again.
          </p>
        </div>
      )}
      {loadingState === loadState.LOADING && (
        <div className="modal__overflow-section util-pad-all-xxxl">
          <AnimatedTireLogo />
        </div>
      )}
      {loadingState === loadState.LOADED && storeList != null && (
        <StoreList
          stores={storeList}
          zipCode={searchedZipCode}
          handleStoreSelection={handleStoreSelection}
          loadMore={handleLoadMore}
        />
      )}
    </>
  );
};

StoreSelector.propTypes = {
  zipCode: propTypes.string,
  setZipCode: propTypes.func,
};

export { StoreSelector };
